/** Elevation along a route line, sampled from the state's terrain
 * PMTiles (terrarium-encoded raster-dem — the manifest's `terrain` layer).
 * Pure math (terrarium decode, bilinear, tile addressing) is exported for
 * tests; network + bitmap decode live behind the TileLoader seam. */

import { densify } from "./geo";
import type { ElevationSample, LonLat } from "./types";

/** Decoded raster tile — what getImageData returns, without the DOM type. */
export interface RasterTile {
  width: number;
  height: number;
  /** RGBA, row-major. */
  data: Uint8ClampedArray;
}

/** Fetches + decodes one terrain tile; null when the tile doesn't exist. */
export type TileLoader = (z: number, x: number, y: number) => Promise<RasterTile | null>;

/** Terrarium: ele = (R*256 + G + B/256) - 32768, meters. */
export function terrariumEleM(r: number, g: number, b: number): number {
  return r * 256 + g + b / 256 - 32768;
}

export interface TerrainSampler {
  /** Elevation profile along the line: a sample roughly every `stepM`
   * meters plus every vertex. Rejects only on total failure; individual
   * missing tiles yield gaps (samples skipped). */
  sampleLine(line: LonLat[], stepM?: number): Promise<ElevationSample[]>;
}

// Below this, a decoded terrarium value is the phone's no-data sentinel
// (raw 0/0/0 terrarium pixels decode to -32768) — any corner at or under
// it drops the sample rather than interpolating across a hole.
const NO_DATA_BELOW_M = -1000;

// Tiles cached per sampler instance, keyed "z/x/y"; capped so a very long
// route (many distinct tiles) can't grow this unboundedly.
const TILE_CACHE_CAP = 64;

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** Fractional Web Mercator tile coordinates (the standard OSM slippy-map
 * formula): integer part is the tile index, fractional part is position
 * within that tile. */
function tileFrac(lon: number, lat: number, zoom: number): { x: number; y: number } {
  const n = 2 ** zoom;
  const x = ((lon + 180) / 360) * n;
  const latRad = (lat * Math.PI) / 180;
  const y =
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;
  return { x, y };
}

function decodeAt(tile: RasterTile, x: number, y: number): number {
  const i = (y * tile.width + x) * 4;
  return terrariumEleM(tile.data[i], tile.data[i + 1], tile.data[i + 2]);
}

/** Elevation at one point via bilinear interpolation of a tile's decoded
 * corners, or null when the tile is missing or any corner is no-data. */
function sampleTile(tile: RasterTile, fracX: number, fracY: number): number | null {
  // Pixel-center convention: pixel i's value represents the point at
  // continuous coordinate i+0.5, so the tile-space fraction is shifted by
  // half a pixel before flooring. Clamp to [0, size-1] at tile edges — no
  // neighbor-tile fetch, the sub-pixel error at a seam is negligible.
  const px = clamp(fracX * tile.width - 0.5, 0, tile.width - 1);
  const py = clamp(fracY * tile.height - 0.5, 0, tile.height - 1);
  const x0 = Math.floor(px);
  const y0 = Math.floor(py);
  const x1 = Math.min(x0 + 1, tile.width - 1);
  const y1 = Math.min(y0 + 1, tile.height - 1);
  const tx = px - x0;
  const ty = py - y0;

  const e00 = decodeAt(tile, x0, y0);
  const e10 = decodeAt(tile, x1, y0);
  const e01 = decodeAt(tile, x0, y1);
  const e11 = decodeAt(tile, x1, y1);
  if (e00 < NO_DATA_BELOW_M || e10 < NO_DATA_BELOW_M || e01 < NO_DATA_BELOW_M || e11 < NO_DATA_BELOW_M) {
    return null;
  }

  const top = e00 + (e10 - e00) * tx;
  const bottom = e01 + (e11 - e01) * tx;
  return top + (bottom - top) * ty;
}

/** Sampler over an injected loader — the testable core. */
export function samplerWith(loader: TileLoader, zoom: number): TerrainSampler {
  const cache = new Map<string, RasterTile | null>();
  const inflight = new Map<string, Promise<RasterTile | null>>();

  function getTile(z: number, x: number, y: number): Promise<RasterTile | null> {
    const key = `${z}/${x}/${y}`;
    if (cache.has(key)) {
      // Touch for LRU recency: delete + re-set moves it to the young end
      // of the Map's iteration order.
      const hit = cache.get(key) as RasterTile | null;
      cache.delete(key);
      cache.set(key, hit);
      return Promise.resolve(hit);
    }
    const pending = inflight.get(key);
    if (pending) return pending;

    const p = loader(z, x, y)
      .then((tile) => {
        cache.set(key, tile);
        if (cache.size > TILE_CACHE_CAP) {
          const oldest = cache.keys().next().value;
          if (oldest !== undefined) cache.delete(oldest);
        }
        return tile;
      })
      .finally(() => {
        inflight.delete(key);
      });
    inflight.set(key, p);
    return p;
  }

  return {
    async sampleLine(line: LonLat[], stepM = 12): Promise<ElevationSample[]> {
      const pts = densify(line, stepM);
      const results = await Promise.all(
        pts.map(async (pt) => {
          const [lon, lat] = pt.at;
          const { x: xf, y: yf } = tileFrac(lon, lat, zoom);
          const tileX = Math.floor(xf);
          const tileY = Math.floor(yf);
          const tile = await getTile(zoom, tileX, tileY);
          if (!tile) return null;
          const ele = sampleTile(tile, xf - tileX, yf - tileY);
          if (ele === null) return null;
          const sample: ElevationSample = { at: pt.at, distM: pt.distM, eleM: ele };
          return sample;
        }),
      );
      return results.filter((s): s is ElevationSample => s !== null);
    },
  };
}

async function decodeImageTile(data: ArrayBuffer): Promise<RasterTile | null> {
  const bitmap = await createImageBitmap(new Blob([data]));
  if (typeof OffscreenCanvas !== "undefined") {
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0);
    const img = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
    return { width: img.width, height: img.height, data: img.data };
  }
  // Older Safari: no OffscreenCanvas.
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0);
  const img = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
  return { width: img.width, height: img.height, data: img.data };
}

/** Browser sampler for a terrain.pmtiles URL. Opens the archive lazily on
 * first sample; picks min(header.maxZoom, 12) as the sampling zoom. */
export function terrainSamplerFor(url: string): TerrainSampler {
  let inner: Promise<TerrainSampler> | null = null;

  async function open(): Promise<TerrainSampler> {
    // A failed header/open is a total failure — let it reject sampleLine.
    const { PMTiles } = await import("pmtiles");
    const archive = new PMTiles(url);
    const header = await archive.getHeader();
    const zoom = Math.min(12, header.maxZoom);
    const loader: TileLoader = async (z, x, y) => {
      // Any single tile's failure (missing, network, decode) is a gap,
      // not a rejection.
      try {
        const res = await archive.getZxy(z, x, y);
        if (!res) return null;
        return await decodeImageTile(res.data);
      } catch {
        return null;
      }
    };
    return samplerWith(loader, zoom);
  }

  return {
    sampleLine(line: LonLat[], stepM?: number): Promise<ElevationSample[]> {
      if (!inner) inner = open();
      return inner.then((sampler) => sampler.sampleLine(line, stepM));
    },
  };
}
